import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { Workflow, Loader2, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnrollAutomationDialog = ({ ids, open, onOpenChange, onDone }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(null);

  const fetchAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/automations`);
      setAutomations(res.data.automations || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchAutomations(); }, [open, fetchAutomations]);

  const enroll = async (a) => {
    setEnrolling(a.id);
    try {
      const res = await axios.post(`${API}/automations/${a.id}/enroll`, { contact_ids: ids });
      toast.success(`Enrolled ${res.data.enrolled} of ${res.data.requested} into "${a.name}"`);
      onOpenChange(false);
      if (onDone) onDone();
    } catch { toast.error('Failed to enroll'); }
    finally { setEnrolling(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="enroll-automation-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
            <Workflow className="w-5 h-5 text-violet-600" /> Enroll in automation
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add {ids.length} selected contact{ids.length === 1 ? '' : 's'} to an automation journey. Active automations start immediately; drafts/paused queue until activated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2 max-h-[45vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          ) : automations.length === 0 ? (
            <p className="text-xs text-slate-600 italic">No automations yet. Create one on the Automations page first.</p>
          ) : automations.map(a => (
            <button
              key={a.id}
              onClick={() => enroll(a)}
              disabled={enrolling === a.id}
              className="w-full flex items-center justify-between bg-slate-950 border border-slate-800 hover:border-violet-700 rounded-md px-3 py-2 text-left transition-colors"
              data-testid={`enroll-into-${a.id}`}
            >
              <span className="flex items-center gap-2 text-sm text-white">
                <Zap className="w-3.5 h-3.5 text-violet-600" /> {a.name}
              </span>
              <span className="text-xs text-slate-500 capitalize">
                {enrolling === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : a.status}
              </span>
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnrollAutomationDialog;
